import type {
  ActiveAssetDataResponse,
  ClearinghouseStateResponse,
  FrontendOpenOrdersResponse,
  MetaAndAssetCtxsResponse,
  MetaResponse,
  PerpConciseAnnotationsResponse,
  SpotClearinghouseStateResponse,
  UserAbstractionResponse,
  UserFillsResponse,
} from '@nktkas/hyperliquid/api/info';
import type {
  BboEvent,
  CandleEvent,
  L2BookEvent,
  TradesEvent,
} from '@nktkas/hyperliquid/api/subscription';

export type PerpMarket = MetaResponse['universe'][number] & {
  /** Exchange asset id (`a` in actions); see `perpAssetIndex` for the HIP-3 encoding. */
  assetIndex: number;
  /** HIP-3 deployer display name; undefined for validator-operated markets. */
  deployer?: string;
  /**
   * HIP-3 fee scale the deployer adds on top of Hyperliquid's, a decimal string; only present
   * on HIP-3 markets. Returned by `meta` but missing from the SDK's `MetaResponse` type.
   */
  deployerFeeScale?: string;
};
export type PerpMarketCtx = MetaAndAssetCtxsResponse[1][number];
/**
 * Deployer-supplied classification and search hints. Only HIP-3 markets are annotated;
 * validator-operated markets have none and are crypto.
 */
export type PerpAnnotation = PerpConciseAnnotationsResponse[number][1];
export type PerpAccountState = ClearinghouseStateResponse;
/**
 * Hyperliquid account abstraction mode. Unified and portfolio-margin accounts keep USDC
 * and its holds in the spot clearinghouse, and their perps states' `withdrawable` is always 0.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/trading/account-abstraction-modes
 */
export type PerpAccountMode = UserAbstractionResponse;
export const holdsUsdcInSpot = (mode: PerpAccountMode) =>
  mode === 'unifiedAccount' || mode === 'portfolioMargin';
export type PerpSpotState = SpotClearinghouseStateResponse;
/** Per-user, per-market trading limits: current leverage plus [long, short] buying power. */
export type PerpActiveAssetData = ActiveAssetDataResponse;
export type PerpPosition = ClearinghouseStateResponse['assetPositions'][number]['position'];
export type PerpOpenOrder = FrontendOpenOrdersResponse[number];
export type PerpFill = UserFillsResponse[number];
export type PerpBook = L2BookEvent;
export type PerpBookLevel = L2BookEvent['levels'][0][number];
/** Unaggregated [best bid, best ask]; either side is null when empty. */
export type PerpBbo = BboEvent['bbo'];
export type PerpTrade = TradesEvent[number];
export type PerpCandle = CandleEvent;

export type PerpSide = 'long' | 'short';
/** Orders that open, grow, shrink or flip a position on the chosen side. */
export type PerpEntryOrderType = 'market' | 'limit';
/** Orders that rest until the mark crosses a trigger price; they always close the open position. */
export type PerpCloseOrderType = 'takeProfit' | 'stopMarket' | 'stopLimit';
export type PerpOrderType = PerpEntryOrderType | PerpCloseOrderType;
